import { useCallback, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

/**
 * Something opened over the plane, moved by its head.
 *
 * The offset is from where it opens and lasts while it is open — a view, not
 * data — unless [kept] starts it somewhere else and hears where each move
 * ends. A press on a control in the head, or on something the head carries
 * out by native drag, is that thing's own and never starts a move. Its head
 * stays on screen, so it can always be taken back.
 */
export function useMovable(enabled = true, kept?: { from: Offset; onMoved: (offset: Offset | null) => void }) {
  const [offset, setOffset] = useState<Offset>(() => kept?.from ?? HOME);
  const [moving, setMoving] = useState(false);
  const last = useRef(offset);
  last.current = offset;
  const from = useRef<{
    x: number;
    y: number;
    ox: number;
    oy: number;
    base: DOMRect;
  } | null>(null);

  const end = () => {
    if (from.current) kept?.onMoved(last.current);
    from.current = null;
    setMoving(false);
  };

  /** Back where it opens, and nothing kept. */
  const reset = useCallback(() => {
    setOffset(HOME);
    kept?.onMoved(null);
  }, [kept]);

  const handle = enabled
    ? {
        onPointerDown: (event: ReactPointerEvent<HTMLElement>) => {
          if (event.button !== 0) return;
          const target = event.target as HTMLElement;
          if (target.closest('button, a, input, textarea, [draggable="true"]')) return;
          const box = event.currentTarget.parentElement?.getBoundingClientRect();
          if (!box) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          from.current = { x: event.clientX, y: event.clientY, ox: offset.x, oy: offset.y, base: box };
          setMoving(true);
        },
        onPointerMove: (event: ReactPointerEvent<HTMLElement>) => {
          const f = from.current;
          if (!f) return;
          const dx = event.clientX - f.x;
          const dy = event.clientY - f.y;
          // Where it would be, held so its head can still be reached.
          const left = clamp(f.base.left + dx, 80 - f.base.width, window.innerWidth - 80);
          const top = clamp(f.base.top + dy, 0, window.innerHeight - 56);
          setOffset({ x: f.ox + left - f.base.left, y: f.oy + top - f.base.top });
        },
        onPointerUp: end,
        onPointerCancel: end,
      }
    : {};

  /** Put where [at] says, without counting as a move: nothing is kept. */
  const place = useCallback((at: Offset) => setOffset(at), []);

  return { offset, moving, handle, reset, place };
}

export interface Offset {
  x: number;
  y: number;
}

const HOME: Offset = { x: 0, y: 0 };

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(v, hi));
}
